/**
 * Fullscreen and landscape, for a touch battle.
 *
 * A phone browser's chrome is a URL bar at the top and a gesture area at the
 * bottom, and both are where a thumb control wants to be. Worse, the bottom edge
 * is a *system* gesture: a swipe up from it goes home, so a fire button sitting
 * there is a button that occasionally closes the game. Fullscreen removes both,
 * which is why a touch battle is expected to be fullscreen rather than merely
 * nicer in it.
 *
 * ── Everything here is best effort, and none of it may throw ────────────────
 *
 * Fullscreen needs a user gesture and can be refused outright; orientation lock
 * is unimplemented on iOS Safari entirely and rejects on a desktop. A battle must
 * start regardless — the alternative is a player who cannot play because their
 * browser declined a presentation request, which is a far worse failure than a
 * URL bar. So each call is wrapped, each refusal is *counted* rather than logged,
 * and `immersiveDiagnostics()` answers what actually happened. That is the same
 * shape as `InputState.lockDiagnostics`, and for the same reason: "it did not go
 * fullscreen" is otherwise unanswerable without guessing.
 *
 * ── The state is read from the browser, never remembered ────────────────────
 *
 * `fullscreen` mirrors `document.fullscreenElement` through the browser's own
 * `fullscreenchange`, and is deliberately not a flag this module sets when it
 * asks. Fullscreen is left in ways nothing here can see — Escape, the system back
 * gesture, a swipe down on Android, another tab taking over — so a remembered
 * boolean goes stale within seconds and a settings toggle built on it lies about
 * the state of the window. Same discipline as `InputState.pointerLocked` reading
 * `document.pointerLockElement` live rather than tracking a
 * `pointerlockchange`: there is one authority, and it is the document.
 */
import { computed, ref } from 'vue';

let fullscreenAttempts = 0;
let fullscreenRefusals = 0;
let orientationAttempts = 0;
let orientationRefusals = 0;
let lastError: string | null = null;

/**
 * The orientation API, as it actually exists across engines.
 *
 * Not `extends ScreenOrientation`: the DOM lib types `lock` and `unlock` as
 * present and non-optional, which is exactly the claim that is false — iOS Safari
 * implements neither. Feature detection has to be able to fail, so the shape is
 * declared independently with both members optional.
 */
interface OrientationApi {
  type?: string;
  lock?: (orientation: 'landscape' | 'portrait' | 'any') => Promise<void>;
  unlock?: () => void;
}

function orientationApi(): OrientationApi | undefined {
  if (typeof screen === 'undefined') return undefined;
  return (screen as unknown as { orientation?: OrientationApi }).orientation;
}

const fullscreenState = ref(false);

/** Whether the document is presenting fullscreen, live. */
export const fullscreen = computed(() => fullscreenState.value);

function readFullscreen(): void {
  fullscreenState.value =
    typeof document !== 'undefined' && document.fullscreenElement !== null;
}

/**
 * Whether asking is worth offering at all.
 *
 * `fullscreenEnabled` is the one that matters and the one people forget: it is
 * `false` inside an iframe without `allow="fullscreen"`, and on iOS Safari, which
 * implements the API for `<video>` and not for arbitrary elements. Offering a
 * control there would be offering a button that cannot work — the rule that kept
 * the dead number-key row off the HUD.
 */
export function immersiveSupported(): boolean {
  if (typeof document === 'undefined') return false;
  return (
    document.fullscreenEnabled === true &&
    typeof document.documentElement.requestFullscreen === 'function'
  );
}

/**
 * Already launched from a Home Screen icon rather than a Safari tab.
 *
 * There is no API that grants a page real fullscreen on iOS Safari — the
 * platform simply does not implement `requestFullscreen` for anything but a
 * `<video>` — so `immersiveSupported()` is correctly `false` there and stays
 * `false` forever. The one thing that *does* remove the address bar and the
 * tab strip entirely is launching the page from an icon added to the Home
 * Screen (`apple-mobile-web-app-capable` in `index.html`), which runs as its
 * own chrome-less process. This is how the UI tells the two states apart, so
 * it can stop asking someone who already did that and stop suggesting it to
 * someone who is browsing normally on a platform that does support the real
 * API.
 *
 * Two reads because the two engines expose it differently: `display-mode` is
 * the standard media query every engine answers; `navigator.standalone` is
 * Safari's own boolean and predates the standard query on iOS.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const media =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosFlag = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(media || iosFlag);
}

/**
 * Worth offering the Home Screen route at all: a touch device that cannot
 * reach real fullscreen and has not already taken the Home Screen route.
 *
 * Deliberately not a browser sniff. `!immersiveSupported()` is what is
 * actually true of iOS Safari (and of an embedding iframe without
 * `allow="fullscreen"`) — the same feature-detection this module already
 * uses for the button it hides, extended to the alternative it can offer
 * instead of nothing.
 */
export function homeScreenOfferable(touch: boolean): boolean {
  return touch && !immersiveSupported() && !isStandalone();
}

// Registered once, at module scope, because the answer is a property of the
// document rather than of any component: a panel that mounted its own listener
// would report correctly while open and know nothing the moment it closed.
if (typeof document !== 'undefined') {
  readFullscreen();
  document.addEventListener('fullscreenchange', readFullscreen);
}

/**
 * Asks for fullscreen and landscape. Resolves either way.
 *
 * **Must be called from a user gesture**, which on the deploy path it is — the
 * player pressed Deploy. Called from anywhere else (a timer, a socket event) the
 * browser refuses and the refusal is counted.
 */
export async function enterImmersive(target?: HTMLElement | null): Promise<void> {
  if (typeof document === 'undefined') return;

  const element = target ?? document.documentElement;
  if (document.fullscreenElement === null && element.requestFullscreen) {
    fullscreenAttempts += 1;
    try {
      // `navigationUI: 'hide'` is a hint and is ignored where unsupported.
      await element.requestFullscreen({ navigationUI: 'hide' });
    } catch (err) {
      fullscreenRefusals += 1;
      lastError = err instanceof Error ? err.message : String(err);
    }
  }

  // Attempted *after* fullscreen, deliberately: on Android Chrome an orientation
  // lock is only granted to a fullscreen document, so the other order refuses
  // every time and looks like the API being unavailable.
  const orientation = orientationApi();
  if (orientation?.lock) {
    orientationAttempts += 1;
    try {
      await orientation.lock('landscape');
    } catch (err) {
      orientationRefusals += 1;
      lastError = err instanceof Error ? err.message : String(err);
    }
  }
}

/** Gives both back. Safe to call when neither was ever granted. */
export async function exitImmersive(): Promise<void> {
  if (typeof document === 'undefined') return;
  const orientation = orientationApi();
  try {
    orientation?.unlock?.();
  } catch {
    // Unlocking something that was never locked is not a problem worth raising.
  }
  if (document.fullscreenElement !== null) {
    try {
      await document.exitFullscreen();
    } catch {
      // Leaving a battle must not be blocked by a presentation request.
    }
  }
}

/**
 * The settings control: in if out, out if in.
 *
 * Takes the same `target` as `enterImmersive` and defaults the same way, so a
 * toggle and the deploy path present the identical element — presenting a
 * *different* one while already fullscreen is a no-op that reads as a dead
 * button.
 */
export async function toggleImmersive(target?: HTMLElement | null): Promise<void> {
  if (typeof document === 'undefined') return;
  if (document.fullscreenElement !== null) {
    await exitImmersive();
    return;
  }
  await enterImmersive(target);
}

export function immersiveDiagnostics(): {
  fullscreen: boolean;
  fullscreenAttempts: number;
  fullscreenRefusals: number;
  orientation: string | null;
  orientationAttempts: number;
  orientationRefusals: number;
  lastError: string | null;
} {
  return {
    fullscreen: typeof document !== 'undefined' && document.fullscreenElement !== null,
    fullscreenAttempts,
    fullscreenRefusals,
    orientation: orientationApi()?.type ?? null,
    orientationAttempts,
    orientationRefusals,
    lastError,
  };
}
